import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  ArrowLeft, 
  Save, 
  Upload,
  X,
  Image as ImageIcon
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const AlertEdit = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  
  const [formData, setFormData] = useState({
    title: '',
    note: '',
    image: null,
    plan: '',
    discountPlan: '',
    isFree: false,
    createdDate: new Date().toISOString().split('T')[0]
  })
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [dragActive, setDragActive] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [currentImageUrl, setCurrentImageUrl] = useState('')
  const { getAlerts, updateAlert, uploadImage } = useFirebase()

  useEffect(() => {
    loadAlert()
  }, [id])

  const loadAlert = async () => {
    try {
      setInitialLoading(true)
      const alerts = await getAlerts()
      const foundAlert = alerts.find(a => a.id === id)
      if (foundAlert) {
        setFormData({
          title: foundAlert.title || '',
          note: foundAlert.message || '',
          image: null,
          plan: foundAlert.plan || '',
          discountPlan: foundAlert.discountPlan || '',
          isFree: foundAlert.plan === 'Free' || foundAlert.plan === '' || foundAlert.isFree || false,
          createdDate: foundAlert.createdDate || new Date().toISOString().split('T')[0]
        })
        setCurrentImageUrl(foundAlert.imageUrl || '')
      } else {
        toast.error('Note not found')
        navigate('/alerts')
      }
    } catch (error) {
      console.error('Error loading note:', error)
      toast.error('Failed to load note')
      navigate('/alerts')
    } finally {
      setInitialLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    if (name === 'isFree') {
      setFormData(prev => ({
        ...prev,
        isFree: checked,
        plan: checked ? '' : prev.plan
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0]
      if (file.type.startsWith('image/')) {
        setFormData(prev => ({ ...prev, image: file }))
        toast.success('Image uploaded successfully!')
      } else {
        toast.error('Please upload an image file')
      }
    }
  }, [])

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      if (file.type.startsWith('image/')) {
        setFormData(prev => ({ ...prev, image: file }))
        toast.success('Image uploaded successfully!')
      } else {
        toast.error('Please upload an image file')
      }
    }
  }

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: null }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.title.trim() || !formData.note.trim()) {
      toast.error('Please fill in title and note')
      return
    }

    try {
      setLoading(true)
      console.log('🚀 Starting note update process...')
      
      let imageUrl = currentImageUrl
      
      // Upload new image if provided
      if (formData.image) {
        try {
          console.log('📤 Uploading new image to Firebase Storage...')
          setUploadProgress(25)
          
          imageUrl = await uploadImage(formData.image, 'alerts')
          setUploadProgress(75)
          
          console.log('✅ New image uploaded successfully:', imageUrl)
        } catch (uploadError) {
          console.error('❌ Image upload failed:', uploadError)
          toast.error('Image upload failed: ' + uploadError.message)
          setLoading(false)
          return
        }
      }
      
      setUploadProgress(90)
      
      // Prepare updated alert data
      const updateData = {
        title: formData.title.trim(),
        message: formData.note.trim(),
        imageUrl: imageUrl || '',
        plan: formData.isFree ? 'Free' : formData.plan || '',
        discountPlan: formData.discountPlan || '',
        isFree: formData.isFree,
        createdDate: formData.createdDate
      }

      console.log('💾 Updating note in Firestore:', updateData)

      await updateAlert(id, updateData)
      
      setUploadProgress(100)
      console.log('✅ Note updated successfully!')
      toast.success('Note updated successfully!')
      navigate('/alerts')
    } catch (error) {
      console.error('Error updating note:', error)
      toast.error('Failed to update note')
    } finally {
      setLoading(false)
      setUploadProgress(0)
    }
  }

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/alerts')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Edit Note</h1>
        </div>
      </div>

      {/* Form */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Note Details</h3>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Image Upload - Drag & Drop */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Image
              </label>
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
                  dragActive 
                    ? 'border-blue-400 bg-blue-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileInput}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                
                {formData.image ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <ImageIcon className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {formData.image.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {(formData.image.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeImage}
                      className="p-1 hover:bg-red-100 rounded-full transition-colors"
                    >
                      <X className="h-5 w-5 text-red-600" />
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="input w-full"
                placeholder="Enter alert title..."
                required
              />
            </div>

            {/* Note (Message) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note *
              </label>
              <textarea
                name="note"
                value={formData.note}
                onChange={handleChange}
                rows={4}
                className="input w-full"
                placeholder="Enter your note..."
                required
              />
            </div>

            {/* Plan */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plan
              </label>
              <div className="space-y-3">
                {/* Free Checkbox */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isFree"
                    name="isFree"
                    checked={formData.isFree}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="isFree" className="ml-2 text-sm text-gray-700">
                    Free 
                  </label>
                </div>
                
                {/* Amount Input */}
                <input
                  type="text"
                  name="plan"
                  value={formData.plan}
                  onChange={handleChange}
                  disabled={formData.isFree}
                  className={`input w-full ${formData.isFree ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="Enter plan amount (e.g., 50 rs, 100 rs)..."
                />
              </div>
            </div>

            {/* Discount Plan */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Discount Plan
              </label>
              <input
                type="text"
                name="discountPlan"
                value={formData.discountPlan}
                onChange={handleChange}
                className="input w-full"
                placeholder="Enter discount plan (e.g., 20% off, 30 rs discount)..."
              />
            </div>

            {/* Created Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Created Date
              </label>
              <input
                type="date"
                name="createdDate"
                value={formData.createdDate}
                onChange={handleChange}
                className="input w-full"
              />
            </div>

            {/* Progress Bar */}
            {loading && uploadProgress > 0 && (
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Updating Note...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/alerts')}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="small" />
                    <span>Updating Note...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AlertEdit
